import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  Button,
  Tooltip,
  Chip,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer
} from '@mui/material';
import {
  Close as CloseIcon,
  ContentCopy as CopyIcon,
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  DataObject as DataIcon,
  History as HistoryIcon,
  CalendarToday as DateIcon,
  TableChart as TableIcon
} from '@mui/icons-material';
import moment from 'moment';
import './DataViewerModal.css';

// JSON Tree Viewer Component
const JsonViewer = ({ data, searchTerm = '' }) => {
  const [expandedKeys, setExpandedKeys] = useState(new Set());

  const toggleExpand = (key) => {
    const newExpanded = new Set(expandedKeys);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setExpandedKeys(newExpanded);
  };

  const highlightText = (text) => {
    if (!searchTerm) return text;
    const parts = text.split(new RegExp(`(${searchTerm})`, 'gi'));
    return parts.map((part, i) =>
      part.toLowerCase() === searchTerm.toLowerCase() ? (
        <span key={i} className="highlight">{part}</span>
      ) : (
        part
      )
    );
  };

  const renderValue = (value, key = '', level = 0) => {
    if (value === null) return <span className="json-null">null</span>;
    if (value === undefined) return <span className="json-undefined">undefined</span>;
    if (typeof value === 'boolean') return <span className="json-boolean">{value.toString()}</span>;
    if (typeof value === 'number') return <span className="json-number">{value}</span>;
    if (typeof value === 'string') return <span className="json-string">"{highlightText(value)}"</span>;

    if (Array.isArray(value)) {
      const isExpanded = expandedKeys.has(key);
      return (
        <span>
          <span className="json-toggle" onClick={() => toggleExpand(key)}>
            {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />} [{value.length}]
          </span>
          {isExpanded && (
            <div style={{ marginLeft: 20 }}>
              {value.map((item, index) => (
                <div key={index}>
                  <span className="json-key">{index}:</span> {renderValue(item, `${key}.${index}`, level + 1)}
                </div>
              ))}
            </div>
          )}
        </span>
      );
    }

    if (typeof value === 'object') {
      const isExpanded = expandedKeys.has(key);
      const keys = Object.keys(value);
      return (
        <span>
          <span className="json-toggle" onClick={() => toggleExpand(key)}>
            {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />} {`{${keys.length}}`}
          </span>
          {isExpanded && (
            <div style={{ marginLeft: 20 }}>
              {keys.map((k) => (
                <div key={k}>
                  <span className="json-key">{highlightText(k)}:</span> {renderValue(value[k], `${key}.${k}`, level + 1)}
                </div>
              ))}
            </div>
          )}
        </span>
      );
    }

    return <span>{JSON.stringify(value)}</span>;
  };

  return <div className="json-viewer">{renderValue(data, 'root')}</div>;
};

// Table-style JSON Viewer
const TableJsonViewer = ({ data, searchTerm = '' }) => {
  const [expandedRows, setExpandedRows] = useState(new Set());

  if (!data || typeof data !== 'object') {
    return <Typography variant="body2" color="text.secondary">No structured data</Typography>;
  }

  const toggleRowExpansion = (key) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setExpandedRows(newExpanded);
  };

  // Enhanced flattening function that handles arrays and special types
  const flattenJSON = (obj, parentKey = '', res = {}) => {
    for (let key in obj) {
      if (!obj.hasOwnProperty(key)) continue;

      const newKey = parentKey ? `${parentKey}.${key}` : key;
      const value = obj[key];

      // Handle null values
      if (value === null) {
        res[newKey] = { type: 'null', value: null };
        continue;
      }

      // Handle arrays
      if (Array.isArray(value)) {
        if (value.length === 0) {
          res[newKey] = { type: 'array', value: '[]' };
        } else if (value.every(item => typeof item !== 'object' || item === null)) {
          // If all items are primitives, display as comma-separated
          res[newKey] = { type: 'array', value: value };
        } else {
          // If items are objects, flatten each item
          value.forEach((item, index) => {
            if (typeof item === 'object' && item !== null) {
              flattenJSON(item, `${newKey}[${index}]`, res);
            } else {
              res[`${newKey}[${index}]`] = { type: typeof item, value: item };
            }
          });
        }
        continue;
      }

      // Handle objects
      if (typeof value === 'object') {
        // Check if it's a date-like string or Date object
        if (value instanceof Date || (typeof value === 'string' && !isNaN(Date.parse(value)) && /^\d{4}-\d{2}-\d{2}/.test(value))) {
          res[newKey] = { type: 'date', value: value };
        } else {
          // Recursively flatten nested objects
          flattenJSON(value, newKey, res);
        }
        continue;
      }

      // Handle primitives
      res[newKey] = { type: typeof value, value: value };
    }
    return res;
  };

  // Format value based on its type
  const formatValue = (valueObj, rowKey) => {
    if (!valueObj) return 'undefined';

    const { type, value } = valueObj;
    const isExpanded = expandedRows.has(rowKey);

    switch (type) {
      case 'null':
        return <span style={{ color: '#999', fontStyle: 'italic' }}>null</span>;

      case 'boolean':
        return (
          <span style={{
            color: value ? '#16A34A' : '#DC2626',
            fontWeight: 'bold'
          }}>
            {String(value)}
          </span>
        );

      case 'number':
        return <span style={{ color: '#2563EB' }}>{value}</span>;

      case 'date':
        try {
          const date = new Date(value);
          return (
            <span style={{ color: '#7C3AED' }}>
              {moment(date).format('YYYY-MM-DD HH:mm:ss')}
            </span>
          );
        } catch {
          return String(value);
        }

      case 'array':
        if (Array.isArray(value)) {
          return (
            <span style={{ color: '#059669' }}>
              [{value.map(v => typeof v === 'string' ? `"${v}"` : String(v)).join(', ')}]
            </span>
          );
        }
        return String(value);

      case 'string':
        // Check if it's a URL
        if (typeof value === 'string' && (value.startsWith('http://') || value.startsWith('https://'))) {
          return (
            <a href={value} target="_blank" rel="noopener noreferrer" style={{ color: '#2563EB' }}>
              {value}
            </a>
          );
        }
        // Check if it's a long string
        if (typeof value === 'string' && value.length > 100) {
          return (
            <Box>
              <Box
                component="span"
                sx={{
                  whiteSpace: isExpanded ? 'pre-wrap' : 'nowrap',
                  wordBreak: isExpanded ? 'break-word' : 'normal',
                  display: 'block'
                }}
              >
                {isExpanded ? value : `${value.substring(0, 100)}...`}
              </Box>
              <Button
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleRowExpansion(rowKey);
                }}
                sx={{
                  mt: 0.5,
                  fontSize: '11px',
                  textTransform: 'none',
                  minWidth: 'auto',
                  padding: '2px 8px'
                }}
              >
                {isExpanded ? (
                  <>
                    <ExpandLessIcon fontSize="small" sx={{ mr: 0.5 }} />
                    Show less
                  </>
                ) : (
                  <>
                    <ExpandMoreIcon fontSize="small" sx={{ mr: 0.5 }} />
                    Show more
                  </>
                )}
              </Button>
            </Box>
          );
        }
        return String(value);

      default:
        return String(value);
    }
  };

  const flatData = flattenJSON(data);

  const filteredEntries = Object.entries(flatData).filter(([key, valueObj]) => {
    if (!searchTerm) return true;
    const value = valueObj?.value;
    return (
      key.toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(value).toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  if (filteredEntries.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
        No data found matching "{searchTerm}"
      </Typography>
    );
  }

  return (
    <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{
              fontWeight: 'bold',
              backgroundColor: 'var(--sa-dark-surface-light)',
              width: '40%',
              minWidth: '200px'
            }}>
              Key
            </TableCell>
            <TableCell sx={{
              fontWeight: 'bold',
              backgroundColor: 'var(--sa-dark-surface-light)',
              width: '60%'
            }}>
              Value
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredEntries.map(([key, valueObj]) => (
            <TableRow key={key} hover>
              <TableCell sx={{
                fontFamily: 'monospace',
                fontSize: '12px',
                verticalAlign: 'top',
                wordBreak: 'break-word',
                width: '40%',
                minWidth: '200px'
              }}>
                {key}
              </TableCell>
              <TableCell sx={{
                fontFamily: 'monospace',
                fontSize: '12px',
                verticalAlign: 'top',
                wordBreak: 'break-word',
                width: '60%'
              }}>
                {formatValue(valueObj, key)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const DataViewerModal = ({ open, onClose, activity }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [copiedText, setCopiedText] = useState('');
  const [viewMode, setViewMode] = useState('table'); // 'json' | 'table'

  if (!activity || !activity.data) {
    return null;
  }

  let parsedData = activity.data;
  if (typeof activity.data === 'string') {
    try {
      parsedData = JSON.parse(activity.data);
    } catch (e) {
      parsedData = activity.data;
    }
  }

  const handleCopy = (data) => {
    navigator.clipboard.writeText(JSON.stringify(data, null, 2));
    setCopiedText('Copied!');
    setTimeout(() => setCopiedText(''), 2000);
  };

  const getSourceBadgeColor = (source) => {
    const sourceColors = {
      twilio: 'primary',
      sendgrid: 'secondary',
      webhook: 'default',
      api: 'info',
      integration: 'warning',
      workflow: 'success'
    };
    return sourceColors[source?.toLowerCase()] || 'default';
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { height: '80vh', maxHeight: '80vh' } }}
      className="history-box-modal"
    >
      <DialogTitle sx={{ m: 0, p: 2 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={2}>
            <DataIcon />
            <Typography variant="h6">External Data Viewer</Typography>
            {activity.dataSource && (
              <Chip
                label={activity.dataSource}
                size="small"
                color={getSourceBadgeColor(activity.dataSource)}
              />
            )}
          </Box>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
            <Tab icon={<DataIcon />} label="Current Data" />
            {activity.dataHistory && activity.dataHistory.length > 0 && (
              <Tab icon={<HistoryIcon />} label={`History (${activity.dataHistory.length})`} />
            )}
          </Tabs>
        </Box>

        {activeTab === 0 && (
          <div className="jason-tab-popup">
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Box>
                {activity.dataReceivedAt && (
                  <Box display="flex" alignItems="center" gap={1}>
                    <DateIcon fontSize="small" color="action" />
                    <Typography variant="caption" color="text.secondary">
                      Received: {moment(activity.dataReceivedAt).format('LLLL')}
                    </Typography>
                  </Box>
                )}
              </Box>
              <Box display="flex" gap={1} alignItems="center">
                <TextField
                  size="small"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
                <Tooltip title={copiedText || 'Copy to clipboard'}>
                  <button
                    className="btn-style-new"
                    variant="outlined"
                    startIcon={<CopyIcon />}
                    onClick={() => handleCopy(parsedData)}
                  >
                    Copy
                  </button>
                </Tooltip>

                <Button
                  size="small"
                  variant={viewMode === 'table' ? 'contained' : 'outlined'}
                  onClick={() => setViewMode('table')}
                  startIcon={<TableIcon />}
                >
                  Table
                </Button>
                <Button
                  size="small"
                  variant={viewMode === 'json' ? 'contained' : 'outlined'}
                  onClick={() => setViewMode('json')}
                  startIcon={<DataIcon />}
                >
                  JSON
                </Button>

              </Box>
            </Box>

            <Paper
              elevation={1}
              sx={{ p: 2, backgroundColor: 'var(--sa-dark-surface-light)', overflow: 'auto', maxHeight: '50vh' }}
            >
              {viewMode === 'json' ? (
                <JsonViewer data={parsedData} searchTerm={searchTerm} />
              ) : (
                <TableJsonViewer data={parsedData} searchTerm={searchTerm} />
              )}
            </Paper>
          </div>

        )}

        {activeTab === 1 && activity.dataHistory && (
          <div className="jason-tab-popup">
            {/* View Mode toggle buttons */}
            <Box className="filter-table-select">
              <button
                className={`filters-button ${viewMode === 'json' ? 'active' : ''}`}
                onClick={() => setViewMode('json')}
              >
                JSON
              </button>
              <button
                className={`filters-button ${viewMode === 'table' ? 'active' : ''}`}
                onClick={() => setViewMode('table')}
              >
                Table
              </button>
            </Box>
            {activity.dataHistory.map((history, index) => (
              <Paper key={index} elevation={1} sx={{ p: 2, mb: 2 }}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={1}
                  flexWrap="wrap"
                  gap={1}
                >
                  <Box>
                    <Typography variant="subtitle2">
                      {moment(history.receivedAt).format('LLLL')}
                    </Typography>
                    {history.source && (
                      <Chip
                        label={history.source}
                        size="small"
                        color={getSourceBadgeColor(history.source)}
                      />
                    )}
                  </Box>

                  <Box display="flex" alignItems="center" gap={1}>
                    {/* Copy JSON button */}
                    <Tooltip title="Copy JSON">
                      <IconButton
                        size="small"
                        onClick={() => {
                          const historyData =
                            typeof history.data === 'string'
                              ? (() => {
                                try {
                                  return JSON.parse(history.data);
                                } catch {
                                  return history.data;
                                }
                              })()
                              : history.data;
                          handleCopy(historyData);
                        }}
                      >
                        <CopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                {/* JSON/Table Viewer */}
                <Box
                  sx={{
                    backgroundColor: 'var(--secondary-color)',
                    p: 1,
                    borderRadius: 1,
                    overflow: 'auto',
                  }}
                >
                  {viewMode === 'json' ? (
                    <JsonViewer
                      data={
                        typeof history.data === 'string'
                          ? (() => {
                            try {
                              return JSON.parse(history.data);
                            } catch {
                              return history.data;
                            }
                          })()
                          : history.data
                      }
                      searchTerm={searchTerm}
                    />
                  ) : (
                    <TableJsonViewer
                      data={
                        typeof history.data === 'string'
                          ? (() => {
                            try {
                              return JSON.parse(history.data);
                            } catch {
                              return history.data;
                            }
                          })()
                          : history.data
                      }
                      searchTerm={searchTerm}
                    />
                  )}
                </Box>
              </Paper>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog >
  );
};

export default DataViewerModal;
